import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import WorkBagForm from "./WorkBagForm.tsx";
import headerStyles from "../../components/WorkBag/WorkBagHeader.module.scss";
import styles from "./JobboardProfileOnboarding.module.scss";
import { getSkills } from "../../store/actions";
import { openEdifySpinner, openMainModal } from "../../store/actions/profileActions";
import { workBagFormValidationSchema } from "../../utils/workBag/workBagFormValidation.ts";
import { saveJobboardProfile } from "../../services/jobboardRegistrationService.ts";
import type { WorkBagFormValues } from "../../types/workBag.ts";
import * as actionTypes from "../../store/actions/types";
import { fireAnalytics } from "../../utils/firebase";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import {
  userHasClientRole,
  userIsJobboardOnlyAccount,
} from "../../utils/jobboard/jobboardProfileGate.ts";

interface UserSkill {
  skill?: { id?: string | number; name?: string };
}

interface UserJobBoardZone {
  allCountry?: boolean;
  city?: string | null;
  jurisdiccion?: { id?: string | number; name?: string };
}

interface UserDataShape {
  name?: string;
  last_name?: string;
  username?: string;
  phone?: string;
  skills?: UserSkill[];
  jobBoardZones?: UserJobBoardZone[];
  categoryConnect?: { id?: string | number }[];
  rols?: { name?: string }[];
}

interface JobboardProfileOnboardingProps {
  userData: UserDataShape | null;
  skills: { id: number; name: string }[];
  getSkills: () => void;
}

function buildTodayDdMmYyyy(): string {
  const t = new Date();
  const dd = String(t.getDate()).padStart(2, "0");
  const mm = String(t.getMonth() + 1).padStart(2, "0");
  const yyyy = String(t.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

function buildInitialValues(userData: UserDataShape | null): WorkBagFormValues {
  const skillsFromApi = (userData?.skills ?? [])
    .map((s) => ({
      id: Number(s.skill?.id),
      name: String(s.skill?.name ?? ""),
    }))
    .filter((s) => !Number.isNaN(s.id) && Boolean(s.name));

  const workZonesFromApi =
    userData?.jobBoardZones
      ?.filter((z) => !z.allCountry)
      ?.map((z) => ({
        name: z.jurisdiccion?.name ?? "",
        city: z.city ?? "",
        jurisdiccion: {
          id: Number(z.jurisdiccion?.id),
          name: z.jurisdiccion?.name ?? "",
        },
      })) ?? [];

  return {
    name: userData?.name ?? "",
    lastname: userData?.last_name ?? "",
    email: userData?.username ?? "",
    phone: userData?.phone ?? "",
    skills: skillsFromApi,
    workZone: workZonesFromApi,
    city: "",
    categoryConnect: (() => {
      const cc = userData?.categoryConnect;
      if (Array.isArray(cc)) {
        return cc.map((c) => Number(c?.id ?? c)).filter((id) => Number.isFinite(id));
      }
      return [];
    })(),
    unemploymentFund: false,
    currentJob: false,
    employmentEndDate: "",
    termsAndConditions: false,
    selectedJurisdiction: null,
  };
}

const JobboardProfileOnboarding: React.FC<JobboardProfileOnboardingProps> = ({
  userData,
  skills,
  getSkills,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isPhone, setIsPhone] = useState(false);
  const today = useMemo(() => buildTodayDdMmYyyy(), []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsPhone(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    getSkills();
  }, [getSkills]);

  const initialValues = useMemo(
    () => buildInitialValues(userData),
    [userData]
  );

  const formik = useFormik<WorkBagFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: workBagFormValidationSchema,
    validateOnMount: false,
    onSubmit: async (values, { setSubmitting }) => {
      dispatch(openEdifySpinner(true));
      try {
        const updatedUser = await saveJobboardProfile(values);
        dispatch({ type: actionTypes.GET_DATA, payload: updatedUser });
        fireAnalytics("jobboard_profile_completion_completed", {
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          category: "onboarding",
          action: "work_bag_profile_completed",
          section: "jobboard_public",
        });
        navigate("/bolsadetrabajo/registro-exitoso");
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
          "No hemos podido guardar tu perfil. Intentá de nuevo en unos minutos.";
        dispatch(
          openMainModal({
            title: "¡Ups!",
            iconType: "error",
            body: message,
            isSuccess: true,
            confirmHandler: null,
          })
        );
      } finally {
        dispatch(openEdifySpinner(false));
        setSubmitting(false);
      }
    },
  });

  if (!userData) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Cargando…</div>
      </div>
    );
  }

  if (!userIsJobboardOnlyAccount(userData)) {
    return userHasClientRole(userData) ? (
      <Navigate to="/dashboard" replace />
    ) : (
      <Navigate to="/bolsadetrabajo" replace />
    );
  }

  return (
    <div className={styles.page}>
      <header className={headerStyles.header}>
        <div className={headerStyles.headerContent}>
          <p className={headerStyles.kicker}>Bolsa de trabajo</p>
          <h1 className={headerStyles.title}>
            Completá tu perfil para que podamos mostrarte ofertas acordes
          </h1>
        </div>
      </header>
      <main className={styles.main}>
        <WorkBagForm
          formik={formik}
          skills={skills}
          today={today}
          isPhone={isPhone}
          submitRequiresDirty={false}
          captchaReady={true}
          useCaptcha={false}
          validatingCaptcha={false}
        />
      </main>
    </div>
  );
};

const mapStateToProps = (state: {
  general: { userData: UserDataShape | null };
  register: { skills: { id: number; name: string }[] };
}) => ({
  userData: state.general.userData,
  skills: state.register.skills,
});

export default connect(mapStateToProps, { getSkills })(JobboardProfileOnboarding);
